import { defineNuxtPlugin, useLayoutMode, useState } from '#imports'
import {
    csrfTokenBootstrapperInjectionKey,
    graphqlClientInjectionKey
} from '~/composables/graphql'
import type { H3Event } from 'h3'
import { useRequestHeaders, useRouter } from '#app'
import { AwesomeGraphQLClient } from 'awesome-graphql-client'
import { type TypedDocumentNode } from '@graphql-typed-document-node/core'
import { print } from 'graphql'
import { getNewTokenFromCapacitor } from '~/composables/mobile/capacitor-bridge'
import {
    bootstrapPublicCsrf,
    getLaravelPublicEndpoint
} from '~/server/utils/public-csrf'
import { setBearerToken } from '~/composables/mobile/mobile-app-data'
import { usePostMessageCapacitor } from '~/composables/mobile/post-message'
import { CSRF_TOKEN_STATE_KEY } from '~/composables/use-state-constants'

async function mobileFetch(
    router: ReturnType<typeof useRouter>,
    postMobileAccessTokenUnauthorized: () => void,
    url: string,
    options?: RequestInit
) {
    // Perform original fetch
    let response = await fetch(url, options)

    // Intercept 401 Unauthorized
    if (response.status === 401) {
        // Pause execution and get new token from Capacitor
        let newToken = ''
        try {
            newToken = await getNewTokenFromCapacitor(
                postMobileAccessTokenUnauthorized
            )
        } catch {
            router.replace('/')
            setBearerToken(null)
            alert(
                'Sorry, the app is having trouble finding your login credentials'
            )
            throw new Error()
        }
        setBearerToken(newToken)

        // Clone the original request options but swap in the new token
        const newOptions = { ...options }
        newOptions.headers = {
            ...newOptions.headers,
            Authorization: 'Bearer ' + newToken
        }

        // Retry the original fetch with the new token
        response = await fetch(url, newOptions)

        // If the retry still returns 401, something went wrong
        if (response.status === 401) {
            router.replace('/')
            setBearerToken(null)
            alert(
                'Sorry, we are having difficulty refreshing your login credentials'
            )
            throw new Error()
        }
    }

    return response
}

async function serverFetchWithPublicCsrf(
    event: H3Event,
    url: string,
    options?: RequestInit
) {
    const bootstrap = await bootstrapPublicCsrf(event)
    const headers = new Headers(options?.headers)

    // SSR GraphQL posts use the same CSRF contract as browser posts, including
    // the Laravel session cookie created by the bootstrap endpoint.
    if (bootstrap.csrfToken !== null) {
        headers.set('X-CSRF-TOKEN', bootstrap.csrfToken)
    }
    if (bootstrap.cookieHeader !== '') {
        headers.set('cookie', bootstrap.cookieHeader)
    }

    return fetch(url, {
        ...options,
        headers
    })
}

export default defineNuxtPlugin(nuxt => {
    const { isMobileApp } = useLayoutMode()
    const csrfToken = useState<string | null>(CSRF_TOKEN_STATE_KEY, () => null)

    // This promise belongs to one Nuxt app instance: one browser tab on the
    // client or one render request on the server. It is never shared globally.
    let pendingCsrfBootstrap: Promise<string | null> | null = null

    async function bootstrapCsrfToken(): Promise<string | null> {
        if (pendingCsrfBootstrap === null) {
            // Multiple Vue Query refreshes can discover one expired session at
            // once; share one request so they all use the same Laravel session.
            pendingCsrfBootstrap = requestCsrfToken()
        }

        const currentBootstrap = pendingCsrfBootstrap
        try {
            return await currentBootstrap
        } finally {
            // An older waiter must not clear a newer bootstrap that began after
            // the older request settled.
            if (pendingCsrfBootstrap === currentBootstrap) {
                pendingCsrfBootstrap = null
            }
        }
    }

    async function requestCsrfToken(): Promise<string | null> {
        const abortController = new AbortController()
        const timeout = setTimeout(() => {
            // A stalled bootstrap must not block every GraphQL request sharing
            // this Nuxt app's pending bootstrap promise indefinitely.
            abortController.abort()
        }, 15_000)

        let response: Response
        try {
            response = await fetch('/csrf-bootstrap', {
                method: 'GET',
                // The token must be bound to the same CPX session cookie that
                // the following GraphQL POST will send back to Laravel.
                credentials: 'same-origin',
                // A cached bootstrap response can contain a token for an
                // expired session, so always retrieve it from Laravel.
                cache: 'no-store',
                headers: {
                    'Cache-Control': 'no-cache',
                    Pragma: 'no-cache'
                },
                signal: abortController.signal
            })
        } catch {
            // Preserve read-only requests during bootstrap transport failures;
            // mutation requests still fail closed through Laravel's middleware.
            return null
        } finally {
            clearTimeout(timeout)
        }

        if (!response.ok) {
            // Keep the token absent so Laravel rejects a following mutation
            // instead of sending a credential from an unrelated session.
            return null
        }

        return response.headers.get('x-csrf-token')
    }

    if (process.client && !isMobileApp.value) {
        function refreshCsrfTokenForCurrentSession(): void {
            // Cached SSR HTML and back-forward-cache restores can retain a token
            // after the CPX session cookie expired or was replaced elsewhere. Keep
            // the current token usable during revalidation: a mismatched session
            // fails closed with a 419 and follows the existing one-time retry path.
            void bootstrapCsrfToken().then(token => {
                // A transient bootstrap failure must not discard a token that
                // remains valid for the current session.
                if (token !== null) {
                    csrfToken.value = token
                }
            })
        }

        // Revalidate the SSR-hydrated token on every browser app startup so a
        // disk- or intermediary-cached document cannot preserve a stale token.
        refreshCsrfTokenForCurrentSession()

        window.addEventListener('pageshow', event => {
            if (event.persisted) {
                refreshCsrfTokenForCurrentSession()
            }
        })
    }

    let client: AwesomeGraphQLClient<TypedDocumentNode>
    if (process.server && nuxt.ssrContext) {
        const headers = useRequestHeaders()
        const event = nuxt.ssrContext.event
        client = new AwesomeGraphQLClient({
            endpoint: getLaravelPublicEndpoint(event, '/graphql-public'),
            fetchOptions: { headers },
            formatQuery: (query: TypedDocumentNode) => print(query),
            fetch: (url: string, options?: RequestInit) =>
                serverFetchWithPublicCsrf(event, url, options)
        })
    } else {
        const { postMobileAccessTokenUnauthorized } = usePostMessageCapacitor()
        const router = useRouter()

        client = new AwesomeGraphQLClient({
            endpoint: '/graphql-public',
            formatQuery: (query: TypedDocumentNode) => print(query),
            fetch: isMobileApp.value
                ? (url: string, options?: RequestInit) => {
                      return mobileFetch(
                          router,
                          postMobileAccessTokenUnauthorized,
                          url,
                          options
                      )
                  }
                : undefined
        })
    }

    nuxt.vueApp.provide(graphqlClientInjectionKey, client)
    nuxt.vueApp.provide(csrfTokenBootstrapperInjectionKey, bootstrapCsrfToken)
})
